import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Calendar, Clock, Users, CalendarDays } from "lucide-react";
import apiClient from "../api/apiClient";
import axios from "axios"; // Import axios directly

const Registration = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    nom_etudiant: "",
    prenom_etudiant: "",
    email_etudiant: "",
    numero_tel_etudiant: "",
    adresse_etudiant: "",
    ville_etudiant: "",
    pays_etudiant: "",
    groupe: "",
    date_debut_formation: "",
    date_fin_formation: "",
    heure_debut: "",
    heure_fin: "",
  });

  const [formationDetails, setFormationDetails] = useState(null);
  const [groupDetails, setGroupDetails] = useState([]);
  const [groupDays, setGroupDays] = useState({});
  const [selectedGroupe, setSelectedGroupe] = useState(null);

  const [alert, setAlert] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [initialError, setInitialError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchFormationAndGroupDetails = async () => {
    try {
      setInitialLoading(true);
      setInitialError(null);

      // Use axios directly for CancelToken
      const source = axios.CancelToken.source();
      const timeoutId = setTimeout(
        () => source.cancel("Request timeout"),
        10000
      );

      // Fetch formation details
      const formationResponse = await apiClient.get(`/formations/${id}`, {
        cancelToken: source.token,
      });
      setFormationDetails(formationResponse.data);

      // Fetch group details
      const groupResponse = await apiClient.get(`/groupes?id_formation=${id}`, {
        cancelToken: source.token,
      });
      setGroupDetails(groupResponse.data);

      // Fetch group days
      const groupDaysResponse = await apiClient.get(`/group_jour`, {
        cancelToken: source.token,
      });
      const groupDaysMap = groupDaysResponse.data.reduce((acc, groupDay) => {
        if (!acc[groupDay.id_groupe]) acc[groupDay.id_groupe] = [];
        acc[groupDay.id_groupe].push({
          nom_jour: groupDay.nom_jour,
          activite: groupDay.activite,
          date: groupDay.Date_Groupe,
        });
        return acc;
      }, {});
      setGroupDays(groupDaysMap);

      if (groupResponse.data?.length > 0)
        setSelectedGroupe(groupResponse.data[0]);

      clearTimeout(timeoutId);
    } catch (err) {
      // Use axios.isCancel directly
      if (axios.isCancel(err)) {
        setInitialError(
          new Error(
            "Délai d'attente dépassé : Veuillez vérifier votre connexion"
          )
        );
      } else if (err.response) {
        setInitialError(
          new Error(
            `Erreur du serveur : ${err.response.status} - ${
              err.response.data?.message || "Erreur inconnue"
            }`
          )
        );
      } else if (err.request) {
        setInitialError(
          new Error(
            "Erreur réseau : Impossible d'atteindre le serveur. Veuillez vérifier votre connexion."
          )
        );
      } else {
        setInitialError(new Error(`Erreur : ${err.message}`));
      }
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchFormationAndGroupDetails();
  }, [id, retryCount]);

  const handleRetry = () => setRetryCount((prev) => prev + 1);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    if (submitError) setSubmitError(null);
  };

  const handleGroupSelection = (groupe) => {
    setSelectedGroupe(groupe);
    setFormData((prevData) => ({
      ...prevData,
      groupe: groupe.ID_Groupe,
      date_debut_formation: groupe.date_debut,
      date_fin_formation: groupe.date_fin,
      heure_debut: groupe.Heure_Debut,
      heure_fin: groupe.Heure_Fin,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nom_etudiant || !formData.prenom_etudiant) {
      setAlert({
        type: "error",
        message: "Veuillez remplir tous les champs obligatoires",
      });
      return;
    }

    setLoading(true);
    setSubmitError(null);
    setAlert({ type: "", message: "" });

    try {
      // Use axios directly for CancelToken
      const source = axios.CancelToken.source();
      const timeoutId = setTimeout(
        () => source.cancel("Request timeout"),
        15000
      );

      // Check group capacity
      const groupResponse = await apiClient.get(
        `/groupes/${selectedGroupe.ID_Groupe}`,
        { cancelToken: source.token }
      );
      const groupData = groupResponse.data;

      if (groupData.Effectif_Actuel >= groupData.Capacite) {
        setAlert({
          type: "info",
          message:
            "Ce groupe est complet. Vous allez être redirigé vers la liste d'attente.",
        });
        setTimeout(
          () =>
            navigate(
              `/reservations/${formationDetails.id_formation}/${selectedGroupe.ID_Groupe}`
            ),
          2000
        );
        return;
      }

      // Step 1: Insert into etudiants
      const etudiantResponse = await apiClient.post(
        `/etudiants`,
        {
          nom_etudiant: formData.nom_etudiant,
          prenom_etudiant: formData.prenom_etudiant,
          email_etudiant: formData.email_etudiant,
          numero_tel_etudiant: formData.numero_tel_etudiant,
          adresse_etudiant: formData.adresse_etudiant,
          ville_etudiant: formData.ville_etudiant,
          pays_etudiant: formData.pays_etudiant,
          date_creation: new Date().toISOString(),
          statut_etudiant: "Actif",
          id_formation: formationDetails.id_formation,
          nom_formation: formationDetails.nom_formation,
        },
        { cancelToken: source.token }
      );

      const id_etudiant = etudiantResponse.data.id;

      // Step 2: Insert into inscriptionsformations
      await apiClient.post(
        `/inscriptionsformations`,
        {
          id_formation: formationDetails.id_formation,
          id_etudiant,
          id_groupe: selectedGroupe.ID_Groupe,
          status_inscription: "En cours",
        },
        { cancelToken: source.token }
      );

      // Step 3: Update group capacity
      await apiClient.patch(
        `/groupes/${selectedGroupe.ID_Groupe}`,
        {
          Effectif_Actuel: groupData.Effectif_Actuel + 1,
        },
        { cancelToken: source.token }
      );

      clearTimeout(timeoutId);

      setAlert({
        type: "success",
        message: "Inscription réussie! Redirection en cours...",
      });
      setTimeout(() => navigate("/"), 5000);
    } catch (error) {
      let errorMessage = "Une erreur est survenue. Veuillez réessayer.";
      // Use axios.isCancel directly
      if (axios.isCancel(error))
        errorMessage =
          "Délai d'attente dépassé : Veuillez vérifier votre connexion et réessayer.";
      else if (error.response)
        errorMessage = `Erreur du serveur : ${error.response.status} - ${
          error.response.data?.message || "Erreur lors de l'inscription"
        }`;
      else if (error.request)
        errorMessage =
          "Erreur réseau : Impossible de contacter le serveur. Veuillez vérifier votre connexion.";
      else errorMessage = `Erreur : ${error.message}`;

      setSubmitError(errorMessage);
      setAlert({ type: "error", message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRetry = () => handleSubmit(new Event("submit"));

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(":");
    return `${hours.padStart(2, "0")}h${minutes}`;
  };

  // Initial loading state
  if (initialLoading && retryCount === 0) {
    return (
      <main className="main-area fix">
        <section className="breadcrumb__area breadcrumb__bg">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <div className="breadcrumb__content">
                  <h3 className="title">Formulaire d'inscription</h3>
                  <nav className="breadcrumb">
                    <span>
                      <a href="/">Accueil</a>
                    </span>
                    <span className="breadcrumb-separator">
                      <i className="fas fa-angle-right"></i>
                    </span>
                    <span>Inscription</span>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="singUp-area section-py-120 text-center py-5">
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          />
          <p className="mt-3">Chargement des détails de la formation...</p>
        </section>
      </main>
    );
  }

  // Initial error state
  if (initialError && !formationDetails) {
    return (
      <main className="main-area fix">
        <section className="breadcrumb__area breadcrumb__bg">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <div className="breadcrumb__content">
                  <h3 className="title">Formulaire d'inscription</h3>
                  <nav className="breadcrumb">
                    <span>
                      <a href="/">Accueil</a>
                    </span>
                    <span className="breadcrumb-separator">
                      <i className="fas fa-angle-right"></i>
                    </span>
                    <span>Inscription</span>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="singUp-area section-py-120">
          <div className="container text-center">
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle fa-2x mb-3 text-danger"></i>
              <h4>Erreur de chargement</h4>
              <p>{initialError.message}</p>
              <div className="d-flex justify-content-center gap-2">
                <button className="btn btn-primary" onClick={handleRetry}>
                  <i className="fas fa-redo me-2"></i>Réessayer
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => navigate("/courses")}
                >
                  Retour aux cours
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }

  // Full registration form JSX
  return (
    <main className="main-area fix">
      <section className="breadcrumb__area breadcrumb__bg">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb__content">
                <h3 className="title">Formulaire d'inscription</h3>
                <nav className="breadcrumb">
                  <span>
                    <a href="/">Accueil</a>
                  </span>
                  <span className="breadcrumb-separator">
                    <i className="fas fa-angle-right"></i>
                  </span>
                  <span>Inscription</span>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="singUp-area section-py-120">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-6 col-lg-8">
              <div className="singUp-wrap">
                <h4 className="title">INSCRIPTIONS A LA FORMATION</h4>

                {formationDetails && (
                  <div className="formation-details mt-3 mb-3 p-3 border rounded">
                    <h4>{formationDetails.nom_formation}</h4>
                    {formationDetails.description_formation && (
                      <p className="mb-0 text-muted">
                        {formationDetails.description_formation.length > 100
                          ? formationDetails.description_formation.substring(
                              0,
                              100
                            ) + "..."
                          : formationDetails.description_formation}
                      </p>
                    )}
                  </div>
                )}

                <p>
                  Bonjour! Prêt à rejoindre? Remplissez les informations
                  ci-dessous.
                </p>

                {submitError && (
                  <div className="alert alert-warning mb-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <small>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {submitError}
                      </small>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={handleSubmitRetry}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            {" "}
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            ></span>{" "}
                            Envoi...{" "}
                          </>
                        ) : (
                          "Réessayer"
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {alert.message && !submitError && (
                  <div
                    className={`alert ${
                      alert.type === "success"
                        ? "alert-success"
                        : alert.type === "info"
                        ? "alert-info"
                        : "alert-danger"
                    }`}
                  >
                    {alert.message}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="account__form">
                  {/* Personal info */}
                  <div className="row gutter-20">
                    <div className="col-md-6">
                      <div className="form-grp">
                        <label htmlFor="nom_etudiant">Nom</label>
                        <input
                          type="text"
                          id="nom_etudiant"
                          name="nom_etudiant"
                          value={formData.nom_etudiant}
                          onChange={handleInputChange}
                          placeholder="Votre nom"
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-grp">
                        <label htmlFor="prenom_etudiant">Prénom</label>
                        <input
                          type="text"
                          id="prenom_etudiant"
                          name="prenom_etudiant"
                          value={formData.prenom_etudiant}
                          onChange={handleInputChange}
                          placeholder="Votre prénom"
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contact info */}
                  <div className="form-grp">
                    <label htmlFor="email_etudiant">Email</label>
                    <input
                      type="email"
                      id="email_etudiant"
                      name="email_etudiant"
                      value={formData.email_etudiant}
                      onChange={handleInputChange}
                      placeholder="Votre email"
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="form-grp">
                    <label htmlFor="numero_tel_etudiant">
                      Numéro de téléphone
                    </label>
                    <input
                      type="tel"
                      id="numero_tel_etudiant"
                      name="numero_tel_etudiant"
                      value={formData.numero_tel_etudiant}
                      onChange={handleInputChange}
                      placeholder="Votre numéro de téléphone"
                      required
                      disabled={loading}
                    />
                  </div>

                  {/* Address info */}
                  <div className="form-grp">
                    <label htmlFor="adresse_etudiant">Adresse</label>
                    <input
                      type="text"
                      id="adresse_etudiant"
                      name="adresse_etudiant"
                      value={formData.adresse_etudiant}
                      onChange={handleInputChange}
                      placeholder="Votre adresse"
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="form-grp">
                    <label htmlFor="ville_etudiant">Ville</label>
                    <input
                      type="text"
                      id="ville_etudiant"
                      name="ville_etudiant"
                      value={formData.ville_etudiant}
                      onChange={handleInputChange}
                      placeholder="Votre ville"
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="form-grp">
                    <label htmlFor="pays_etudiant">Pays</label>
                    <input
                      type="text"
                      id="pays_etudiant"
                      name="pays_etudiant"
                      value={formData.pays_etudiant}
                      onChange={handleInputChange}
                      placeholder="Votre pays"
                      required
                      disabled={loading}
                    />
                  </div>

                  {/* Group selection */}
                  <div className="form-grp">
                    <label className="text-lg font-semibold mb-3">
                      Sélection du Groupe
                    </label>
                    {groupDetails && groupDetails.length > 0 ? (
                      <div className="group-selection space-y-3">
                        {groupDetails
                          .filter(
                            (g) =>
                              g.id_formation === formationDetails.id_formation
                          )
                          .map((groupe) => {
                            const daysForGroup =
                              groupDays[groupe.ID_Groupe] || [];
                            const isSelected =
                              selectedGroupe &&
                              selectedGroupe.ID_Groupe === groupe.ID_Groupe;
                            return (
                              <div
                                key={groupe.ID_Groupe}
                                onClick={() =>
                                  !loading && handleGroupSelection(groupe)
                                }
                                className={`groupe-option p-3 border rounded-lg shadow-sm transition-all duration-300 cursor-pointer ${
                                  isSelected
                                    ? "btn btn-primary text-white shadow-md"
                                    : "bg-white text-gray-800 hover:bg-blue-50 hover:shadow-sm"
                                } ${
                                  loading ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                                style={{
                                  ...(isSelected && {
                                    transform: "scale(1.01)",
                                  }),
                                }}
                              >
                                <div className="flex justify-between items-center mb-1">
                                  <h4
                                    className={`text-lg font-bold ${
                                      isSelected
                                        ? "text-white"
                                        : "text-gray-900"
                                    }`}
                                  >
                                    Groupe {groupe.ID_Groupe}
                                  </h4>
                                </div>
                                <div className="grid grid-cols-2 gap-1">
                                  <div>
                                    <p className="flex items-center mb-1">
                                      <CalendarDays
                                        className={`mr-2 ${
                                          isSelected
                                            ? "text-white"
                                            : "text-blue-500"
                                        }`}
                                        size={16}
                                      />
                                      <span
                                        className={`font-medium ${
                                          isSelected ? "text-white" : ""
                                        }`}
                                      >
                                        Dates: {formatDate(groupe.date_debut)} -{" "}
                                        {formatDate(groupe.date_fin)}
                                      </span>
                                    </p>
                                    <p className="flex items-center">
                                      <Clock
                                        className={`mr-2 ${
                                          isSelected
                                            ? "text-white"
                                            : "text-blue-500"
                                        }`}
                                        size={16}
                                      />
                                      <span
                                        className={`font-medium ${
                                          isSelected ? "text-white" : ""
                                        }`}
                                      >
                                        Horaires:{" "}
                                        {formatTime(groupe.Heure_Debut)} -{" "}
                                        {formatTime(groupe.Heure_Fin)}
                                      </span>
                                    </p>
                                  </div>
                                  <div>
                                    <p className="flex items-center">
                                      <Users
                                        className={`mr-2 ${
                                          isSelected
                                            ? "text-white"
                                            : "text-blue-500"
                                        }`}
                                        size={16}
                                      />
                                      <span
                                        className={`font-medium ${
                                          isSelected ? "text-white" : ""
                                        }`}
                                      >
                                        Capacité: {groupe.Effectif_Actuel}/
                                        {groupe.Capacite}
                                      </span>
                                    </p>
                                  </div>
                                </div>
                                <div className="mt-2">
                                  <ul className="list-disc ml-5 text-sm">
                                    {daysForGroup.map((day) => (
                                      <li
                                        key={day.nom_jour + day.date}
                                        className={
                                          isSelected ? "text-white" : ""
                                        }
                                      >
                                        {day.nom_jour}: {day.activite}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <p className="text-gray-500">
                        Aucun groupe disponible pour cette formation.
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary w-full mt-4"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        {" "}
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        ></span>{" "}
                        Inscription en cours...
                      </>
                    ) : (
                      "S'inscrire"
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Registration;
